import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'
import { article as articleUi } from '@/shared/lib/articleStyles'

type ArticleTocItem = {
    id: string
    label: string
}

type ArticleTocProps = {
    items: ArticleTocItem[]
    label: string
    ariaLabel: string
}

export function ArticleToc({ items, label, ariaLabel }: ArticleTocProps) {
    const [activeId, setActiveId] = useState(items[0]?.id)
    const sectionIds = items.map(item => item.id).join('|')

    useEffect(() => {
        const ids = sectionIds.split('|').filter(Boolean)
        if (ids.length === 0) return

        const elements = ids
            .map(id => document.getElementById(id))
            .filter((el): el is HTMLElement => el != null)

        if (elements.length === 0) return

        const visible = new Map<string, IntersectionObserverEntry>()

        const observer = new IntersectionObserver(
            entries => {
                for (const entry of entries) {
                    if (entry.isIntersecting) visible.set(entry.target.id, entry)
                    else visible.delete(entry.target.id)
                }

                const topmost = [...visible.values()].sort(
                    (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
                )[0]

                if (topmost) setActiveId(topmost.target.id)
            },
            { rootMargin: '-18% 0px -62% 0px', threshold: [0, 0.25, 1] },
        )

        for (const el of elements) observer.observe(el)
        return () => observer.disconnect()
    }, [sectionIds])

    if (items.length === 0) return null

    return (
        <nav className={articleUi.toc} aria-label={ariaLabel}>
            <p className={articleUi.tocLabel}>{label}</p>
            <ol className={articleUi.tocList}>
                {items.map(item => {
                    const isActive = item.id === activeId
                    return (
                        <li key={item.id} className={isActive ? articleUi.tocItemActive : articleUi.tocItem}>
                            <a
                                href={`#${item.id}`}
                                className={cn(articleUi.tocLink, isActive && articleUi.tocLinkActive)}
                                aria-current={isActive ? 'location' : undefined}
                            >
                                {item.label}
                            </a>
                        </li>
                    )
                })}
            </ol>
        </nav>
    )
}
